// First-party marketing attribution for ductcleanco.com
// ------------------------------------------------------
// Captures paid/organic campaign parameters on every page load and client-side
// route change, stores FIRST-TOUCH and LATEST-TOUCH attribution in versioned
// first-party storage (90 days), and exposes:
//   - getFormAttribution()      -> backend/CRM payload (incl. click IDs — server-side only)
//   - getAnalyticsAttribution() -> analytics-safe object for the GTM data layer
//     (NEVER contains PII or advertising click identifiers)
//
// Rules implemented per spec:
//   - first touch is created once, when marketing attribution is first detected,
//     and is never overwritten by internal navigation
//   - latest touch updates only on a genuinely NEW marketing campaign / click id
//   - internal params (e.g. source=service-page) are stored separately and never
//     overwrite utm/campaign/click-id attribution
//   - all analytics values are normalized strings, max 255 chars (Clarity-safe)

const FIRST_KEY = "dcc_first_touch_attribution_v1";
const LATEST_KEY = "dcc_latest_touch_attribution_v1";
const INTERNAL_SOURCE_KEY = "dcc_internal_source_v1"; // sessionStorage, internal only
const SESSION_KEY = "dcc_session_visit_v2"; // sessionStorage — THIS visit only
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

// URL parameters captured when present (spec Part 1)
export const CAMPAIGN_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_content", "utm_term",
  "utm_source_platform", "campaign_id", "campaign_name", "adgroup_id", "adgroup_name",
  "ad_id", "keyword_id", "match_type", "network", "device",
  "loc_physical_id", "loc_interest_id", "asset_id",
  "adset_id", "adset_name",
];

// Advertising click identifiers — backend/CRM ONLY, never the data layer
export const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "msclkid", "fbclid"];

const ALL_PARAMS = [...CAMPAIGN_PARAMS, ...CLICK_ID_PARAMS];

// params that mark an arrival as genuine marketing attribution
const SIGNATURE_PARAMS = ["utm_source", "utm_campaign", "campaign_id", "ad_id", "asset_id", ...CLICK_ID_PARAMS];

const norm = (v) => (v == null ? "" : String(v).trim().slice(0, 255));
const nowIso = () => new Date().toISOString();

function readStore(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.expires_at || Date.parse(obj.expires_at) < Date.now()) {
      window.localStorage.removeItem(key);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

function writeStore(key, obj) {
  try {
    window.localStorage.setItem(key, JSON.stringify(obj));
  } catch {
    /* storage full / blocked — attribution simply isn't persisted */
  }
}

// true when the URL carries any real marketing parameter (not internal ones)
function extractMarketing(search) {
  const q = new URLSearchParams(search || "");
  const found = {};
  let has = false;
  for (const k of ALL_PARAMS) {
    const v = norm(q.get(k));
    if (v) { found[k] = v; has = true; }
  }
  // internal-only params (e.g. source=service-page) — stored apart, never
  // allowed to create or overwrite marketing attribution
  const internalSource = norm(q.get("source"));
  if (internalSource) {
    try { window.sessionStorage.setItem(INTERNAL_SOURCE_KEY, internalSource); } catch { /* ignore */ }
  }
  return has ? found : null;
}

const signatureOf = (obj) =>
  SIGNATURE_PARAMS.map((k) => obj?.[k] || "").join("|");

// Called on every page load AND every client-side route change.
export function captureAttribution(search) {
  getSessionVisit(); // make sure this session's arrival evidence exists first
  const marketing = extractMarketing(search);
  // landing/referrer bypass the 255-char param clip — full ad URLs run long
  const landingPage = String(window.location.href.split("#")[0]).slice(0, 2000);
  const landingPath = norm(window.location.pathname);
  const referrer = String(document.referrer || "").slice(0, 2000);

  // first touch is established on ANY visit — even with zero marketing params —
  // so organic/direct leads still carry landing page, referrer and timestamp
  // (previously a param-less visit stored nothing, leaving those leads blank)
  const first = readStore(FIRST_KEY);
  if (!first) {
    writeStore(FIRST_KEY, {
      ...(marketing || {}),
      initial_landing_page: landingPage,
      initial_landing_path: landingPath,
      initial_referrer: referrer,
      first_visit_timestamp: nowIso(),
      stored_at: nowIso(),
      expires_at: new Date(Date.now() + TTL_MS).toISOString(),
    });
  }

  if (!marketing) return false; // plain navigation never overwrites marketing touches

  const latest = readStore(LATEST_KEY);
  const newSignature = signatureOf(marketing);
  const isNewTouch = !latest || signatureOf(latest) !== newSignature;
  if (isNewTouch) {
    writeStore(LATEST_KEY, {
      ...marketing,
      latest_landing_page: landingPage,
      latest_landing_path: landingPath,
      latest_referrer: referrer,
      latest_visit_timestamp: nowIso(),
      stored_at: nowIso(),
      expires_at: new Date(Date.now() + TTL_MS).toISOString(),
    });
  }
  return true;
}

export function getFirstTouch() {
  return readStore(FIRST_KEY);
}

export function getLatestTouch() {
  return readStore(LATEST_KEY);
}

export function getInternalSource() {
  try { return window.sessionStorage.getItem(INTERNAL_SOURCE_KEY) || ""; } catch { return ""; }
}

const pick = (obj, keys) => {
  const out = {};
  for (const k of keys) out[k] = norm(obj?.[k]);
  return out;
};

// Backend/CRM payload: flat fields = latest touch, falling back to first touch.
// Includes click identifiers — this goes to the first-party backend ONLY.
// ---------------------------------------------------------------------------
// CURRENT-SESSION visit evidence (the fix for stale-touch mislabeling).
// Captured once per browser session (sessionStorage = per tab, dies on close),
// from THIS arrival's URL params and referrer only. A visitor who clicked an
// ad weeks ago but arrived directly today gets an EMPTY marketing snapshot —
// their stored first/latest touches remain as history, never as today's label.
export function getSessionVisit() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* fall through to fresh capture */ }
  const params = new URLSearchParams(window.location.search);
  const rec = {
    session_started_at: nowIso(),
    landing_page: String(window.location.href.split("#")[0]).slice(0, 2000),
    landing_path: norm(window.location.pathname),
    referrer: String(document.referrer || "").slice(0, 2000),
  };
  for (const k of ALL_PARAMS) {
    const v = params.get(k);
    if (v) rec[k] = norm(v);
  }
  try { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(rec)); } catch { /* private mode */ }
  return rec;
}

export function getFormAttribution() {
  const sess = getSessionVisit() || {};
  const first = readStore(FIRST_KEY);
  const latest = readStore(LATEST_KEY);
  const flat = {};
  // campaign params + click ids: CURRENT SESSION ONLY — never fall back to
  // stored touches, or a returning direct visitor keeps their old ad label
  for (const k of ALL_PARAMS) flat[k] = norm(sess[k]);
  // landing/referrer: initial_* stays true first-ever history; latest_* is
  // THIS visit, so the CRM sees where this enquiry actually came from
  flat.initial_landing_page = norm(first?.initial_landing_page) || norm(sess.landing_page);
  flat.initial_landing_path = norm(first?.initial_landing_path) || norm(sess.landing_path);
  flat.initial_referrer = norm(first?.initial_referrer);
  flat.first_visit_timestamp = norm(first?.first_visit_timestamp) || norm(sess.session_started_at);
  flat.latest_landing_page = norm(sess.landing_page);
  flat.latest_landing_path = norm(sess.landing_path);
  flat.latest_referrer = norm(sess.referrer);
  flat.latest_visit_timestamp = norm(sess.session_started_at);
  flat.conversion_page_url = norm(window.location.href.split("#")[0]).slice(0, 500);
  const stripMeta = (o) => {
    if (!o) return null;
    const { stored_at, expires_at, ...rest } = o;
    return rest;
  };
  return {
    flat,
    first_touch_attribution: stripMeta(first),
    latest_touch_attribution: stripMeta(latest),
  };
}

// Analytics-safe attribution (GTM data layer / GA4 / Clarity):
// campaign params only — NO click IDs, NO PII. All strings <= 255 chars.
export function getAnalyticsAttribution({ includePaths = false } = {}) {
  const first = readStore(FIRST_KEY);
  const latest = readStore(LATEST_KEY);
  const out = pick(latest, CAMPAIGN_PARAMS);
  for (const k of CAMPAIGN_PARAMS) if (!out[k]) out[k] = norm(first?.[k]);
  if (includePaths) {
    out.first_landing_path = norm(first?.initial_landing_path) || norm(latest?.latest_landing_path);
    out.latest_landing_path = norm(latest?.latest_landing_path) || norm(first?.initial_landing_path);
  }
  return out;
}

// dedupe marketing_attribution_ready per page view (StrictMode-safe)
let _lastReadyFor = null;

// Push the analytics-safe attribution readiness event (spec Part 7).
export function pushAttributionReady() {
  const first = readStore(FIRST_KEY);
  const latest = readStore(LATEST_KEY);
  if (!first && !latest) return; // nothing captured yet
  const key = `${window.location.pathname}|${window.location.search}`;
  if (_lastReadyFor === key) return;
  _lastReadyFor = key;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "marketing_attribution_ready",
    attribution: getAnalyticsAttribution({ includePaths: true }),
  });
}
